<template>
	<div>
		<jump-strategy v-if="showJumpModal" :widget-data="jumpData" @close-modal="closeModal" />
		<charts-modal
			v-if="showDetailModal"
			:volatility-data="getVolatilityDataWithAssetClass"
			:growth-data="getGrowthData"
			:returns-chart="getAnnualChart.large"
			:returns-chart-data="getAnnualChart.largeData"
			:returns-chart-nav="getAnnualChart.nav"
			:returns-chart-nav-data="getAnnualChart.navData"
			:inception-date="getAnnualData.inceptionDate"
			:max-date="getAnnualData.maxDate"
			:fund-symbol="getFundAttributes.FundSymbol"
			:fund-name="getFundAttributes.FundName"
			:index-name="getFundInfoData.cgIndexName"
			:share-class="getFundAttributes.ShareClass"
			:selected-tab="getSelectedChartTab"
			:audience="getAudience"
			@close-modal="closeModal"
			@change-tab="changeTab"
			@request-chart="requestAnnualReturnsChart"
		>
			<template #superscript3>
				<disclosure-superscript
					class="volatility-modal"
					anchor="modal_volatility"
					:statistics="[`NAVAvg10yr`]"
				/>
			</template>
			<template #superscript4>
				<disclosure-superscript
					class="volatility-modal"
					anchor="modal_volatility_standardDeviation"
					:statistics="[`stdDeviation`]"
				/>
			</template>
			<template #superscript5>
				<disclosure-superscript
					class="volatility-modal"
					anchor="modal_volatility_indexId"
					:statistics="[primaryIndexId]"
				/>
			</template>
			<template #standardDeviation>
				<i-ball-modal tag="SDF" boundary="#modal-scroll-element" />
			</template>
			<template #volatilityIndexId>
				<i-ball-modal :tag="primaryIndexId" boundary="#modal-scroll-element" />
			</template>
			<template #superscript1>
				<disclosure-superscript
					class="returns-modal"
					anchor="modal_annualReturns"
					:statistics="[
						`NAVCumul1Yr`,
						`NAVAvg3yr`,
						`NAVAvg5yr`,
						`NAVAvg10yr`,
						`ARRNAV_15YTD`,
						`NAVAvgLifetime`,
					]"
				/>
			</template>
			<template #annualReturnsIndexId>
				<i-ball-modal :tag="primaryIndexId" boundary="#modal-scroll-element" />
			</template>
			<template #superscript6>
				<disclosure-superscript
					class="growth-modal"
					anchor="modal_growth10K_title"
					:statistics="[`NAVAvgLifetime`]"
				/>
			</template>
			<template #superscript7>
				<disclosure-superscript
					class="growth-modal"
					anchor="modal_growth10K_indexId"
					:statistics="[primaryIndexId]"
				/>
			</template>
			<template #growthIndexId>
				<i-ball-modal :tag="primaryIndexId" boundary="#modal-scroll-element" />
			</template>
		</charts-modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import ChartsModal from '../Modals/ChartsModal';
import JumpModalShareClass from '../Modals/JumpModalShareClass';
import format from '_shared_/plugins/format';
export default {
	name: 'ModalWrapper',
	components: {
		'jump-strategy': JumpModalShareClass,
		ChartsModal
	},
	computed: {
		...mapGetters([
			'getReturnsPageData',
			'showJumpModal',
			'getChartData',
			'getEnvironment',
			'getReturnsLargeChart',
			'getReturnsLargeChartData',
			'getReturnsLargeChartNav',
			'getReturnsLargeChartNavData',
			'getAnnualReturnsData',
			'selectedChartTab',
			'getFundAttributes',
			'getFundInfoData',
			'getAudience',
			'volatilityData',
			'strategies',
			'growth10kData',
			'showDetailModal'
		]),
		getAnnualChart() {
			return {
				large: this.getReturnsLargeChart || '',
				largeData: this.getReturnsLargeChartData || '',
				nav: this.getReturnsLargeChartNav || '',
				navData: this.getReturnsLargeChartNavData || ''
			};
		},
		getAnnualData() {
			const $format = format.formatHelpers;
			const inceptionDate = this.getAnnualReturnsData.inceptionDate || null;
			const maxDate = this.getAnnualReturnsData.maxDate;
			return {
				inceptionDate:
          (inceptionDate &&
            $format.fullDate($format.fromOADate(inceptionDate))) ||
          null,
				maxDate: maxDate && $format.fullDate($format.fromOADate(maxDate)) || null
			};
		},
		getGrowthData() {
			const data = {};
			if (this.growth10kData) {
				data.growthLargeChart = this.growth10kData.growthLargeChart;
				data.shareClass = this.growth10kData.shareClass;
				data.fundSymbol = this.growth10kData.fundSymbol;
				data.indexName = this.growth10kData.indexName;
			}
			if (this.getFundAttributes) {
				data.fundName = this.getFundAttributes.FundName;
			}
			return data;
		},
		getSelectedChartTab() {
			return this.selectedChartTab;
		},
		getVolatilityDataWithAssetClass() {
			return {
				...this.volatilityData,
				assetClass: this?.getFundAttributes?.AssetClass
			};
		},
		jumpData() {
			const data = {};
			if (this.strategies && this.getAudience && this.getFundAttributes) {
				data['funds'] = this.strategies;
				data['audience'] = this.getAudience || 'advisor';
				data['environment'] = this.getEnvironment;
				data['shareClass'] = this.getFundAttributes.ShareClass;
				data['totalObjectives'] = this.getAllObjectivesForAudience();
			}
			return data;
		},
		primaryIndexId() {
			return this?.getFundInfoData?.cgIndexID ?? '';
		}
	},
	watch: {
		showDetailModal(shouldShow) {
			if (shouldShow) {
				document.addEventListener('click', this.clickedOutside);
			}
		}
	},
	async beforeMount() {
		const audience =
			this.$store.state?.appConfig?.context?.userType?.toLowerCase() ===
			'institutional'
				? 'AFIS Institutional'
				: 'AFIS Default';
		await this.$store.dispatch('getAndCommit', {
			endpoint: 'returns-all',
			commitId: 'SET_RETURNS_PAGE',
			dataSet: this.$store.state.stagingFlag,
			audience
		});
	},
	methods: {
		getAllObjectivesForAudience() {
			const returnsObjectives =
				this?.getReturnsPageData?.objectives ?? [];
			return [
				...returnsObjectives
					.reduce((acc, cur) => {
						acc.add(cur.Objective);
						return acc;
					}, new Set([]))
					.values()
			];
		},
		clickedOutside(event) {
			if (event.composedPath()[0].className.indexOf('modal-link') > -1) {
				//Clicking the modal links sometimes counts as a click outside
				//Don't close the modal in this case
				return;
			}
			const modalScroll = document.getElementById('modal-scroll-element');
			if (modalScroll) {
				const isClickInside = modalScroll.contains(event.target);
				if (!isClickInside) {
					this.closeModal();
				}
			}
		},
		changeTab(tab) {
			const modal = { showModal: true, activeTab: tab };
			this.$store.commit('displayModal', modal);
		},
		closeModal() {
			document.removeEventListener('click', this.clickedOutside);
			this.$store.commit('displayModal', false);
		},
		requestAnnualReturnsChart(data) {
			const that = this;
			this.$store
				.dispatch('getAndCommit', {
					endpoint: 'returns-module',
					commitId: 'SET_RETURNS',
					fundNumber: this.getFundAttributes.FundNumber,
					shareClass: this.getFundAttributes.ShareClass,
					audience: this.getAudience,
					stagingFlag: this.$store.state.stagingFlag,
					indexId: this.getFundAttributes.CGIndexID,
					startDate: data.startYear,
					endDate: data.endYear,
					fundSymbol: this.getFundAttributes.FundSymbol
				})
				.then(next => {
					//Watch wasn't playing nicely so update the computed property directly in the then block
					try {
						const chartSrc = next.data.data.charts.largeChart;
						const chartData = next.data.data.charts.largeChartData;
						that.getAnnualChart.large = chartSrc;
						that.getAnnualChart.largeData = chartData;
						that.$forceUpdate();
					} catch (e) {
						//just swallow the error
					}
				});
		}
	}
};
</script>
